// FUNNEL-4 — **THE FIVE FREE TURNS, AND THEY BIND TO THE ACCOUNT RATHER THAN THE BROWSER.**
//
// HER SPEC: a visitor comes through the Ask Lala door, **signs up FIRST** so her chart exists before
// the conversation, lands directly in an open conversation with Lala speaking first, and gets **FIVE
// turns with the full intelligence** — her real chart in context, every gate riding — with a soft
// counter and then a warm wall onto the membership page.
//
// >>> THIS IS NOT THE DAILY CAP AND IT MAY NEVER BE FOLDED INTO IT (R18). <<< `server/lala-cap.js`
// answers *how many turns does this tier get TODAY* and it RESETS every local day. This answers *has
// this account ever had its one free allotment*, and it **never resets**. Folding the two would either
// re-gift five turns to every free account every morning — which is her clause 4's whole worry — or
// make a paying member's daily allowance expire permanently. Two questions, two laws.
//
// >>> AND IT OPENS ONE FEATURE, WHICH IS THE CLAUSE WITH TEETH. <<< An allotment that answered *yes*
// to a bare "is she allowed" question is a back door into every room in the house — the Magic Room,
// the Library, the seals — for anyone who signs up. `freeTurnsOpen` takes the FEATURE KEY and
// compares it against one declared constant, so a second feature joins only by a deliberate edit
// here and never by a caller forgetting to narrow.
//
// IT IMPORTS NOTHING, which is the entry requirement that lets a `.mjs` gate LOAD and RUN every state
// of it with no database, no server and no browser (R146: reach is transitive). The member-facing
// sentences are CATALOG KEYS rather than English, for the same reason `numerology-stages.ts` holds
// keys: importing `t` here would buy the translation and pay for it with the property this whole file
// exists for (R211).

// >>> HER NUMBER, DECLARED ONCE AND READ BY THE GATE, THE COUNTER, THE WALL AND THE GRANT. <<< Five
// spelled four times is four answers to how generous this house is, and the copy that drifts is the
// one on her screen (R28/R29).
export const FREE_TURNS = 5;

// The one feature this allotment may ever open. Ask Lala's own key, and nothing else.
export const FREE_TURN_FEATURE = "lala:chat";

// The catalog keys. `{n}` is the number of turns she has left, and a translation that drops it is
// complete, non-empty and missing the only fact in the sentence (ITEM 14b's placeholder-parity law).
export const FREE_TURN_KEYS = {
  counterOne: "lala.free.counterOne",
  counterMany: "lala.free.counterMany",
  wallTitle: "lala.free.wallTitle",
  wallBody: "lala.free.wallBody",
  wallAction: "lala.free.wallAction",
};

// PRESENCE, NEVER COERCION (R53/R136/R137). Both of these arrive off a database row and then off a
// JSON payload, and `Number(true)` is 1: a coerced count would hand somebody a sixth turn, or — far
// worse in the other direction — read a real 0 as a missing value and re-gift the whole allotment.
const count = (v) => (typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null);

/**
 * What this account's one free allotment is worth right now.
 *
 * `granted` is the STAMP — the fact that an allotment was ever given — and it is a different fact
 * from `used === 0` (R18). Without it, an account that has never been given one and an account that
 * has been given one and spent nothing are the same row, and her clause 4's *one allotment per
 * account EVER* cannot be enforced at all.
 */
export function freeTurnState({ granted = false, used = 0 } = {}) {
  const spentSoFar = count(used);
  // An unreadable count is SPENT, not fresh. This is the one direction that costs money: read the
  // other way, a malformed value re-gifts five full Lala turns on every request that carries it.
  if (spentSoFar === null) {
    return { granted: Boolean(granted), used: FREE_TURNS, remaining: 0, spent: true, unreadable: true };
  }
  if (granted !== true) {
    return { granted: false, used: spentSoFar, remaining: 0, spent: true, unreadable: false };
  }
  const remaining = Math.max(0, FREE_TURNS - spentSoFar);
  return { granted: true, used: spentSoFar, remaining, spent: remaining === 0, unreadable: false };
}

/**
 * May this allotment open this door for this member?
 *
 * FAILS CLOSED ON EVERYTHING: a feature that is not Ask Lala, a tier that is not free, no grant, a
 * spent allotment, an unreadable count. The only way through is a real granted allotment with real
 * turns left, held by a free account.
 *
 * >>> AND A PAID MEMBER NEVER SPENDS ONE, WHICH IS DELIBERATE. <<< She already has the room, so an
 * allotment consumed on her behalf is a thing she cannot see being taken from her — and if she ever
 * lapses to free it is the difference between meeting a warm five-turn welcome and meeting a wall.
 */
export function freeTurnsOpen(state = {}, tier = "free", feature = "") {
  // >>> A DESTRUCTURING DEFAULT APPLIES ONLY TO `undefined`, SO `null` REACHED THE PROPERTY AND
  // RAISED. <<< This runs inside `requireFeature`, where a throw is not a refusal — it is a 500 on
  // the door every free account meets, and the member is told nothing at all (R19: a crash is not a
  // catch). It answers CLOSED, which is this file's direction everywhere.
  if (!state || typeof state !== "object") return false;
  if (feature !== FREE_TURN_FEATURE) return false;
  if (String(tier || "free") !== "free") return false;
  return state.granted === true && state.unreadable !== true && Number(state.remaining) > 0;
}

/**
 * Is this account eligible to BE granted the one allotment?
 *
 * Her clause 7: every signed-up-never-paid account that has **never had a Lala conversation** gains
 * the same five turns. So eligibility is three facts — free tier, never granted before, and no
 * conversation history — and the third is what stops this becoming a gift to members who already
 * used Ask Lala on a membership they have since let lapse.
 */
export function freeTurnsGrantable({ tier = "free", granted = false, hasSpoken = false, funnelOriginated = false } = {}) {
  if (String(tier || "free") !== "free") return false;
  if (granted === true) return false;
  // >>> **A FOURTH FACT, AND HER 2026-08-27 REPORT IS WHY: THE FUNNEL'S WALL WAS REACHING MEMBERS
  // WHO NEVER SAW A FUNNEL.** <<<
  //
  // Measured on live rows before a line was written (R25/R180). Two members hitting this wall on the
  // day she reported it -- 154017 and 153413 -- **both joined on 2026-08-08**, both carry
  // `landing_tag: null`, and NEITHER is named by a funnel lead. They were granted the allotment
  // **that same afternoon**, three weeks after signing up, the first time they reached for Ask Lala.
  // Of the 52 accounts standing at the wall, **22 arrived through no funnel door at all.**
  //
  // The three facts above are all true of any long-standing free member who tries Ask Lala for the
  // first time -- so she was handed a funnel visitor's trial, spent it in one sitting, and met a
  // sentence about five free turns she never asked for. **That is a receipt for something that did
  // not happen (R141)**, which is the same law this file already cites for never telling somebody
  // her five are used up when she never had five.
  //
  // HER ORDER: *"fix so the wall exists ONLY for funnel-originated never-paid accounts and members
  // NEVER meet it."* This is that sentence, and it is the ONE place it can be enforced -- the wall,
  // the counter and the door all hang off whether the allotment was ever granted, so a guard here
  // governs every one of them without a second copy anywhere (R28/R29).
  //
  // **PRESENCE, NEVER TRUTHINESS (R53/R136/R137)**, and the direction is R19's cheap one: an origin
  // this house cannot establish is NOT a funnel arrival, so an unreadable answer costs a stranger a
  // trial she can be given deliberately, where the other way round costs a member the wall.
  if (funnelOriginated !== true) return false;
  return hasSpoken !== true;
}

/**
 * >>> SHE SPENT HER FIVE, SO THE ROOM STAYS OPEN TO **READ** — WHICH IS NOT THE SAME ACT AS SPENDING
 * (R18), AND ONE LAW WAS ANSWERING BOTH. <<<
 *
 * Her clause 8 is *"the wall with the gold button IN the chat to membership"*, and the wall lives at
 * the bottom of her own five-turn conversation. `/api/lala/messages` is a READ of rows she already
 * owns and `/api/lala/chat` is a SPEND that buys a model call, and both sat behind one `lala:chat`
 * key — so refusing the spend refused the read, her thread became unreadable to her, and the payload
 * the wall is drawn from never arrived.
 *
 * THIS IS THE ONE SPELLING OF THAT QUESTION AND BOTH CALLERS ASK IT (R28/R29): `freeTurnsDoorOpen`,
 * so the entitlements payload opens the room, and `requireFeature`'s allotment branch, so the server
 * really serves it. Two spellings would be two answers to *may she read her own conversation*, and
 * the one that drifted would be the one on the arrival read every funnel member makes — which is
 * precisely how her clause 2 greeting came to reach nobody.
 *
 * It DELEGATES the wall itself to `freeTurnWall` rather than re-spelling *granted and nothing left*,
 * because that law already carries the ruling that makes it safe: an account that was never granted
 * one is refused, so the house never tells somebody her five are used up when she never had five
 * (R141). What this adds is the two facts `freeTurnWall` deliberately does not ask — the allotment
 * is a FREE-TIER instrument (`freeTurnsPayload`'s own ruling), and an UNREADABLE count opens
 * nothing, because a door opened on a number nobody could read is a door opened on nothing and
 * refusing costs exactly today's behaviour (R19's cheap direction).
 */
export function freeTurnsWallOpen(state = {}, tier = "free") {
  if (!state || typeof state !== "object") return false;
  if (String(tier || "free") !== "free") return false;
  if (state.unreadable === true) return false;
  return Boolean(freeTurnWall(state));
}

/**
 * >>> WILL THIS DOOR OPEN FOR HER — WHICH IS NOT THE SAME QUESTION AS *DOES SHE HOLD TURNS* (R18).
 * <<<
 *
 * `freeTurnsOpen` answers *she holds turns she can spend*, and it is what the server's final gate
 * and the counter need. This answers *touching Ask Lala will let her in*, which is TRUE one moment
 * EARLIER: for an account that holds no allotment yet and is grantable, the door opens BY BEING
 * TOUCHED, because `requireFeature` grants at exactly that moment.
 *
 * >>> AND THE MISSING SECOND HALF IS WHY HER WHOLE ITEM 2 CHAIN DIED AT ITS LAST STEP. <<< The
 * entitlements payload answered `freeTurnsOpen` alone, so a brand-new funnel arrival read
 * `lala:chat: false` — and W1-1's design is that THE CLIENT REFUSES FIRST, so the arrival read was
 * never made, the greeting never composed, the plum window never painted and the counter never
 * drew. **The grant fires inside the request the client had already decided not to send: a chicken
 * and egg, and the egg is her funnel.** Measured on the live table the day this shipped: 28 distinct
 * visitors reached `/talk` and ZERO allotments were granted, while the ONE grant on the site came
 * through the DRAWER, whose send carries no membership guard at all.
 *
 * >>> IT IS A SECOND LAW RATHER THAN A WIDENING OF THE FIRST, AND THAT IS THE ROUND. <<< Widened,
 * `freeTurnsOpen` would answer TRUE at `requireFeature`'s final gate for an account whose grant had
 * just FAILED to write — she would be let through holding nothing, and `spendFreeTurn`'s own WHERE
 * clause would refuse her mid-turn. Two questions, two laws, and the strict one stays strict.
 *
 * IT FAILS CLOSED EXACTLY WHERE ITS HALVES DO, so nothing new is reachable through it: an account
 * that has spoken before is refused by `hasSpoken`, and an UNREADABLE read answers spent-and-spoken
 * by `freeTurnsFor`'s own failure arms, so a bad minute of database can still hand out nothing.
 *
 * >>> AND ITS THIRD ARM IS HER CLAUSE 8, BECAUSE READING A CONVERSATION IS NOT SPENDING A TURN
 * (R18). <<<
 *
 * Her words are *"the wall with the gold button IN the chat to membership"*. A spent allotment is
 * `granted: true, remaining: 0` FOREVER — the fence that makes one allotment per account EVER never
 * clears `granted_at` and never resets `used`, correctly — so with two arms this law answered
 * FALSE for her, `resolveEntitlements` wrote `lala:chat: false` with a lock naming
 * `needsTier: apprentice`, and **W1-1's client refused FIRST.** The arrival read was never made, so
 * the payload the wall is drawn FROM never arrived and `freeTurnView` — which answers `wall`
 * perfectly — was never asked. **Her wall was composed, correct, and unreachable on every visit
 * after the single render of the fifth answer.**
 *
 * >>> AND THE SERVER'S OWN CHAT BRANCH HAD ALREADY BEEN WRITTEN FOR THE REQUEST THAT COULD NOT
 * ARRIVE. <<< `if (!onFreeTurns && freeWall && capTier === "free")` composes the warm wall reply and
 * spends nothing — and `requireFeature` answered 402 above it, so that branch had never run for
 * anybody. R38/R165's trap twice over on one clause: the wall's render never fed, and the wall's own
 * handler never reached.
 *
 * >>> IT HANDS HER NOTHING SHE MAY NOT HAVE, BY ARITHMETIC RATHER THAN BY CARE. <<< The arm asks
 * `freeTurnWall` — this house's ONE answer to *did she really hold the allotment and really spend
 * it* (R28/R29) — so a free account that was never granted one still meets the ordinary locked door,
 * which is that law's own ruling and is what stops the house telling somebody her five are used up
 * when she never had five (R141). What opens is `/api/lala/`, every route of it scoped to HER OWN
 * rows by the session; `freeTurnsGrantable` still refuses her (`granted === true`), so nothing is
 * re-gifted; `freeTurnsOpen` still refuses her, so `spendFreeTurn` is never reached — and that
 * statement's own `lala_free_turns_used < $2` is the second wall underneath it. **The sixth turn is
 * impossible twice.**
 *
 * AND IT SPELLS THE TIER, WHICH ITS TWO SIBLINGS GET FROM THEIR DELEGATES AND `freeTurnWall` DOES
 * NOT ASK AT ALL — the allotment is a free-tier instrument (`freeTurnsPayload`'s own ruling, one law
 * down) — and it refuses an UNREADABLE count, because a door opened on a number nobody could read is
 * a door opened on nothing and refusing costs exactly today's behaviour (R19's cheap direction).
 */
export function freeTurnsDoorOpen(state = {}, tier = "free", feature = "") {
  if (freeTurnsOpen(state, tier, feature)) return true;
  if (!state || typeof state !== "object") return false;
  if (feature !== FREE_TURN_FEATURE) return false;
  // >>> AND A SHAPE THAT IS NOT AN ALLOTMENT IS NOT A GRANTABLE ONE. <<< A destructuring default
  // applies only to `undefined`, so a bare call defaults to `{}` — and an empty object reads as
  // never-granted and never-spoken, which is grantable. So does `[]`, which is an object too. Every
  // allotment this house builds carries a REAL boolean here (`freeTurnState` writes `Boolean(...)`),
  // so that is the exact test, and it fails closed on all three. THIS ROUND'S OWN FIXTURE CAUGHT IT.
  if (typeof state.granted !== "boolean") return false;
  // ITEM 2 CLAUSE 8 — A SPENT ALLOTMENT STILL OPENS THE ROOM SHE WAS GIVEN. The wall lives at the
  // bottom of her own conversation, and she cannot be shown it through a door that refuses the read.
  if (freeTurnsWallOpen(state, tier)) return true;
  return freeTurnsGrantable({
    tier, granted: state.granted, hasSpoken: state.hasSpoken, funnelOriginated: state.funnelOriginated,
  });
}

/**
 * The soft counter, as a catalog key and its placeholder value.
 *
 * Her words are *"3 more with me, love"*. It draws NOTHING at zero: at zero she is not being counted
 * down any more, she is at the wall, and a counter reading "0 more with me" beside a wall is the
 * house saying the same thing twice in two registers.
 */
export function freeTurnCounter(state = {}) {
  const left = Number(state.remaining);
  if (!Number.isInteger(left) || left <= 0) return null;
  return { key: left === 1 ? FREE_TURN_KEYS.counterOne : FREE_TURN_KEYS.counterMany, n: left };
}

/**
 * The wall, in her voice, at the exact moment desire is highest.
 *
 * It is only ever raised for an account that really HAD the allotment and really spent it. A free
 * account that was never granted one meets the ordinary locked door instead — telling somebody her
 * five turns are used up when she never had five turns is a receipt for something that did not
 * happen (R141), and it reads as the house taking something away.
 */
export function freeTurnWall(state = {}) {
  if (state.granted !== true || Number(state.remaining) > 0) return null;
  return { title: FREE_TURN_KEYS.wallTitle, body: FREE_TURN_KEYS.wallBody, action: FREE_TURN_KEYS.wallAction };
}

/**
 * What rides back to the client on every Ask Lala answer, so the counter and the wall are the
 * SERVER'S answer rather than a number the browser keeps for itself.
 *
 * Her clause 4 is that a refresh changes nothing and a new browser changes nothing. A client-side
 * count is exactly the thing a refresh resets, so the payload is the only carrier and the browser
 * holds no tally of its own.
 *
 * >>> AND THE ALLOTMENT IS A FREE-TIER INSTRUMENT, WHICH THIS LAW DID NOT KNOW AND ITS THREE
 * SIBLINGS ALL DO. <<< `freeTurnsGrantable` and `freeTurnsOpen` each SPELL this exact line and
 * `freeTurnsDoorOpen` DELEGATES to both of them (R28/R29, and the fixture's floor is three rather
 * than four for that reason); `freeTurnsPayload` and `freeTurnWall` asked nothing at all, and
 * the fence that makes one allotment per account EVER — `granted_at` is never cleared and `used`
 * is never reset, correctly — means `granted: true, remaining: 0` is a PERMANENT state.
 *
 * >>> SO A MEMBER WHO SPENT HER FIVE AND THEN PAID THROUGH THE WALL'S OWN GOLD BUTTON KEPT THE WALL
 * FOREVER, AND HER COMPOSER WITH IT. <<< `freeTurnView` answers `wall`, `LalaTurnNotice` draws it,
 * and `composerLock(notice, "", atWall)` returns `disabled: true` — so she paid for Ask Lala and
 * could not type into it, on the one screen ITEM 2's clause (h) is about. TRAP-1b wrote this class
 * down in as many words one door over: *the ACTION is what happens after she pays, and it may not be
 * empty — an empty one leaves a member who has just paid looking at the rope she paid to get past.*
 * Here the action really is `() => {}` at both call sites, so nothing even re-reads.
 *
 * >>> AND THE SERVER'S OWN CHAT BRANCH ALREADY GOT THIS RIGHT, WHICH IS WHY THE LAW HAS TO HOLD IT
 * (R28/R29). <<< `if (!onFreeTurns && freeWall && capTier === "free")` spells the tier test at ONE
 * call site; the thread read spelled none. Two answers to *is she on the allotment*, and the one
 * that drifted is the one on the arrival read every funnel member makes. It is asked HERE now, so
 * every call site inherits it, and the biconditional over those call sites is what makes a sixth
 * one join by going RED rather than by somebody remembering.
 *
 * MEASURED before it was written (R25/R180): 35 real members granted, 6 spent all five, 4 now hold
 * a tier, and **0 are at the wall AND paying** — so this is REACHABLE and NOT YET SUFFERED, which is
 * said plainly rather than dressed up as a rescue (R141). It is reachable by the funnel's own
 * intended path and by no other: spend five, meet the wall, tap the gold button, pay.
 */
export function freeTurnsPayload(state = {}, tier = "free") {
  if (String(tier || "free") !== "free") return null;
  if (state.granted !== true) return null;
  return { granted: true, used: state.used, remaining: state.remaining, of: FREE_TURNS, spent: state.spent === true };
}

// ---------------------------------------------------------------------------
// THE CLIENT'S ONE READ. Everything above answers a question about the ROW; this answers the only
// question the composer has: *what does this member see above her text box right now.*
//
// >>> THE ALLOTMENT OUTRANKS THE DAILY NOTICE, AND THAT IS R18 RATHER THAN A PREFERENCE. <<< A free
// account has ZERO daily turns by that table's own ruling ("Ask Lala is not in Newbie at all; the
// locked door handles this"), so the two can never legitimately both be true — and if they ever are,
// the one that is true ABOUT HER is the five she was given. Read the other way she is told to come
// back at midnight for an allowance that does not exist and never will.
//
// Three answers, never two (R18): `counter` is *you have some left*, `wall` is *you had five and
// they are spent*, and NULL is *this is not a free-turn member at all* — which is every paying member
// on the site and must draw nothing whatever.
export function freeTurnView(payload) {
  if (!payload || typeof payload !== "object" || payload.granted !== true) return null;
  const counter = freeTurnCounter(payload);
  if (counter) return { kind: "counter", ...counter };
  const wall = freeTurnWall(payload);
  if (wall) return { kind: "wall", ...wall };
  return null;
}
/**
 * >>> THE ALLOTMENT OPENED THE SERVER'S DOOR AND THE CLIENT WAS TOLD IT WAS SHUT. <<<
 *
 * MEASURED (R25/R180), on a staged seat holding three turns: `/api/lala/messages` answers **200**
 * with her thread and her counter -- `requireFeature` asks `freeTurnsOpen` and lets her in -- while
 * `/api/membership/entitlements` answers **`features["lala:chat"] = false`** with a lock naming
 * `needsTier: apprentice`. That payload is composed from the FEATURES TABLE, which is a pure
 * function of the TIER and cannot know an allotment exists.
 *
 * **AND W1-1's DESIGN IS THAT THE CLIENT REFUSES FIRST**, so the consequence is not cosmetic: the
 * whole funnel cohort met the purple box at a door the server was holding open, the drawer's own
 * `opensSurface` guard never fired, and FUNNEL-4's clause 2 greeting -- composed, gated and
 * correct -- had **never reached anybody on any surface**. R213's defect one payload over, and its
 * own comment names the promise being broken: *the box she is shown before the request and the box
 * she is shown after one can never disagree.*
 *
 * >>> BOTH HALVES OR NEITHER, WHICH IS R18 RATHER THAN BELT AND BRACES. <<< `featureOutcome` reads
 * `features` and `featureLockFor` reads `locks`, so a decoration that flips one leaves either a
 * surface that mounts and is refused, or a box raised over a room she can already open. A caller
 * doing one of the two reads as a complete repair in every diff.
 *
 * It DECORATES rather than re-resolves (ITEM 12's neutrality argument): `open !== true` hands the
 * entitlements straight back, so every paying member on this site is byte-identical, and only a
 * real `true` moves anything (R53/R136/R137 -- this is a boolean composed from a database read, and
 * a coerced one opens a gated room).
 */
export function allotmentEntitlements(ent, open) {
  if (!ent || typeof ent !== "object" || open !== true) return ent;
  const locks = { ...(ent.locks && typeof ent.locks === "object" ? ent.locks : {}) };
  delete locks[FREE_TURN_FEATURE];
  return { ...ent, features: { ...(ent.features || {}), [FREE_TURN_FEATURE]: true }, locks };
}
